import React, {createRef, useContext, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import {Icon, IconButton, Link, makeStyles, Switch, TablePagination, Typography, useTheme} from "@material-ui/core";
import { Stack } from '@mui/material';
import {
  DatePicker,
  ComboSelect,
  MaskedText,
} from "../../../controls";
import {ColumnWidth, dateSearch, dowSearch} from "../../../blocks/ColumnHelper";

import SystemContext from "../../../SystemContext";
import {
  useScheduledTripTree,
  useAddScheduledTripTree,
  useUpdateScheduledTripTree,
  useDeleteScheduledTripTree,
} from "../../../services/scheduledTrips";

import ScheduledTripLegList from "./ScheduledTripLegList";
import ScheduledTripClients from "./ScheduledTripClients";
import dialogs from "./ScheduledTripListDialogs";
import BuildPrimaryRouteFromLegs from '../utils/BuildPrimaryRouteFromLegs';
import BuildOnwardRouteIconFromLegs from '../utils/BuildOnwardRouteIconFromLegs';
import BuildTruckRouteIconFromLegs from '../utils/BuildTruckRouteIconFromLegs';
import BuildFlightListFromLegs from '../utils/BuildFlightListFromLegs';
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { AirCargoIcon, AirCargoInverseIcon, FlightScheduleIcon } from "../../../icons";
import {AddBox, MonetizationOn, Queue} from "@material-ui/icons";
import axios from "axios";
import useTripDepartmentUsers from "../../../services/auth/useTripDepartmentUsers";
// import AddScheduledTripDialog from './ScheduledTripListDialogs/AddScheduledTripDialog';
import EditScheduledTripDialog from './ScheduledTripListDialogs/EditScheduledTripDialog';
import ContextMenu from "../../../controls/ContextMenu";
import {Preview} from "@mui/icons-material";
import ViewCostingDialog from "../../Costings/components/ViewCostingDialog";
import EditCostingDialog from "../../Costings/components/EditCostingDialog";
import {getCostingByUcn, getCostingDefaultValues} from "../../Costings/includes/costingDialogFunctions";
import CostingsStyles from "../../Costings/styles/CostingsStyles";
import {useUpdateCosting} from "../../../services/costings";

const apiSitePrefix = getApiSitePrefix();

const emptyLeg = {
  airlineCode: null,
  flightNo: null,
  originAirportCode: null,
  destinationAirportCode: null,
}

const formatChars={
  "U": "[A-Za-z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "n": "-",
  "x": "."
}


const ScheduledTripList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const props = {
    ..._props
  }

  const classes = props.classes;
  const costingClasses = makeStyles(CostingsStyles)();

  const tableRef = createRef({state: {lastEditingRow: false}});
  const month = props.month;
  const year = props.year;

  const queryClient = useQueryClient();
  const extraKeys = [];

  const handleAddScheduledTripTree = useAddScheduledTripTree();
  const handleUpdateScheduledTripTree = useUpdateScheduledTripTree();
  const handleDeleteScheduledTripTree = useDeleteScheduledTripTree();
  const handleUpdateCosting = useUpdateCosting();

  const trips = useScheduledTripTree({tableRef, queryClient, extraKeys});
  const lastTrip = trips.data && trips.data.length > 0
    ? trips.data[0].lastTrip : ((year - 2000) * 100000 + month * 1000 - 1);

  // canEdit should be passed from parent in _props
  const [state, setState] = useState({
    canEdit: props.canEdit,
    year: props.year,
    month: props.month,
    showExpired: false,
  });

  const [reset, setReset] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);

  const departmentUsers = useTripDepartmentUsers({queryClient, extraKeys: [defaultValues?.tripOwnerDepartment]});

  const [costingDefaultValues, setCostingDefaultValues] = useState({});
  if (!costingDefaultValues || __.isEmpty(costingDefaultValues)) {
    getCostingDefaultValues(apiSitePrefix, SystemData, setCostingDefaultValues);
  }

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/trip/defaults`);
      if (response.data && response.data.success) {
        setDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Trips Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Trips Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Trip Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Trip Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Trip Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Trip Defaults. Server is not responding`);
    }
  }

  const generateSsim = async () => {

    SystemData.confirm(
      "Generate SSIM",
      "This function sends an updated seasonal SSIM message to all customers and service providers subscribed to the SSIM message.",
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      async (response) => {
        if (response) {
          const url = `${apiSitePrefix}/tripschedule/generatessim`;

          if (url) {
            try {
              const response = await axios.get(url, {
                headers: {
                  //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
                  'Access-Control-Allow-Origin': '*',
                }
              });

              if (response && response.data) {
                if (response.data.success) {
                  SystemData.openAlert(
                    "SSIM Confirmation",
                    `Seasonal SSIM sent to all SSIM subscribers`
                  );
                } else {
                  SystemData.openAlert(
                    "SSIM Error",
                    `The Seasonal SSIM could not be sent due to the following error: ${response.data.error}`
                  );
                }
              }
            } catch (err) {
              SystemData.openAlert(
                "SSIM Error",
                `The Seasonal SSIM could not be sent due to the following unexpected error: ${err}`
              );
            }
          }
        }
      },
      {secondMessage: "Are you sure you wish to send SSIM to all SSIM subscribers?"}
    );
  }

  const viewCosting = async (record) => {
    if (record.uniqueCostingNo) {
      const costingRecord = await getCostingByUcn(record.uniqueCostingNo, apiSitePrefix, SystemData);
      if (costingRecord) {
        eventBus.trigger("ViewCostingDialog.open", costingRecord);
      }
    }
  }

  const editCosting = async (record) => {
    if (record.uniqueCostingNo) {
      const costingRecord = await getCostingByUcn(record.uniqueCostingNo, apiSitePrefix, SystemData);
      if (costingRecord) {
        eventBus.trigger("EditCostingDialog.open", costingRecord);
      }
    }
  }


  const minDate = moment.utc().add(1, 'day');
  const initialDate = minDate.toISOString() ;
  const dateFormat = "DD/MM/YYYY";

  const getTripColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    return [
      {
        ...baseColumn,
        ...ColumnWidth(100),
        field: "depth",
        editable: "never",
        searchable: false,
        render: (rowData) => (<>{rowData.depth > 0?<><div style={{marginLeft: (((rowData.depth - 1) * 20)), width: 1, display: "inline-block"}}></div><Icon>subdirectory_arrow_right</Icon></>:<></>}<Icon>event</Icon></>)
      },
      {
        ...baseColumn,
        ...ColumnWidth(55),
        cellStyle: { padding: "8px 2px 8px 2px" },
        headerStyle: { padding: "8px 2px 8px 2px" },
        title: "Trip Prefix",
        field: "tripNo",
        editable: "never",
        searchable: false,
        render: (rowData) => (<>{rowData.recordType==="SCHEDULE"?process.env.REACT_APP_TRIP_PREFIX || "MAG":""}</>)
      },
      {
        ...baseColumn,
        ...ColumnWidth(90),
        title: "Trip Number",
        field: "tripNo",
        initialEditValue: (lastTrip + 1),
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.padStart(2,'0').toUpperCase();
          const tripNo = rowData.tripNo?`${rowData.tripNo}`.padStart(2,'0'):""
          return tripNo.endsWith(termUpper);
        },
        render: (rowData) => (<>{rowData.recordType==="SCHEDULE" && rowData.tripNo?`${rowData.tripNo}`.padStart(2,'0'):""}</>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(110),
        title: "Start Date",
        field: "startDate",
        editable: props.canEdit ? "onAdd" : "never",
        initialEditValue: initialDate,
        type: "date",
        format: "dd MMM YYYY",
        customFilterAndSearch: dateSearch('startDate'),
        editComponent: (props) => (
          <DatePicker
            minDate={initialDate}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(110),
        title: "End Date",
        field: "endDate",
        editable: props.canEdit ? "onAdd" : "never",
        initialEditValue: initialDate,
        type: "date",
        format: "dd MMM YYYYY",
        customFilterAndSearch: dateSearch('endDate'),
        editComponent: (props) => (
          <DatePicker
            minDate={initialDate}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Frequency",
        field: "frequencyType",
        editable: state.canEdit ? "always" : "never",
        initialEditValue: "1",
        searchable: false,
        render: (rowData) => (<>{rowData.recordType==="SCHEDULE"?
          <ComboSelect
            id="frequency"
            group="FREQUENCY TYPES"
            subGroup=""
            fullWidth
            value={rowData.frequencyType}
            displayOnly={true}
          />
          :""
        }</>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(90),
        title: "Day",
        field: "dow",
        emptyValue:(rowData)=>(rowData.frequencyType==="CALENDAR" && rowData.recordType==="SCHEDULE"?<Icon>date_range</Icon>:null),
        editable: state.canEdit ? "always" : "never",
        customFilterAndSearch: dowSearch('dow'),
        render: (rowData) => (<>{rowData.recordType==="SCHEDULE"?
          <ComboSelect
            id="dow"
            group="DAY OF WEEK"
            subGroup=""
            fullWidth
            value={`${rowData.dow}`}
            displayOnly={true}
          />
          :""
        }</>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(250),
        title: "Route",
        field: "legs",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const legs = rowData.legs;
          const termUpper = `${term}`.toUpperCase();
          for (let i = 0; i < legs.length; i++) {
            const leg = legs[i];
            const {airlineCode, flightNo, originAirportCode, destinationAirportCode} = {...emptyLeg, ...leg};
            if (originAirportCode === termUpper) return true;
            if (destinationAirportCode === termUpper) return true;
            if (`${flightNo}` === termUpper) return true;
            if (`${airlineCode}${flightNo}` === termUpper) return true;
          }
          return false;
        },
        render: (rowData) => (<BuildPrimaryRouteFromLegs legs={rowData.legs}/>)
      },
      {
        ...baseColumn,
        title: "Onward",
        ...ColumnWidth(70),
        field: "legs",
        editable: "never",
        render: (rowData) => (
          <div>
            <BuildOnwardRouteIconFromLegs legs={rowData.legs} style={{paddingRight: 10}}/>
            <BuildTruckRouteIconFromLegs legs={rowData.legs} style={{paddingRight: 10}}/>
          </div>
        )
      },
      {
        ...baseColumn,
        title: "Flights",
        field: "legs",
        editable: "never",
        render: (rowData) => (<BuildFlightListFromLegs legs={rowData.legs}/>)
      },
      // {
      //   ...baseColumn,
      //   title: "Onward",
      //   field: "legs",
      //   editable: "never",
      //   render: (rowData) => (<BuildOnwardRoutesFromLegs legs={rowData.legs}/>)
      // },
      // {
      //   ...baseColumn,
      //   title: "Trucking",
      //   field: "legs",
      //   editable: "never",
      //   render: (rowData) => (<BuildTruckRoutesFromLegs legs={rowData.legs}/>)
      // },
      {
        ...baseColumn,
        ...ColumnWidth(180),
        title: "Account Manager",
        field: "commercialOwnerId",
        editable: "never",
        render: (rowData) => {
          const userInfo = __.find(departmentUsers?.data, {user_id: rowData.commercialOwnerId});
          return (userInfo ? <span>{`${userInfo.firstName} ${userInfo.lastName}`}</span> : <></>)
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Costing Number",
        field: "uniqueCostingNo",
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        searchable: false,
        editComponent: (props) => (
          <MaskedText
            mask="UUU-9999999"
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            defaultValue=""
            onChange={props.onChange}
            inputProps={{style:{fontSize: "0.875rem"}}}
          />
        ),
        render: (rowData) => {
          return rowData.uniqueCostingNo?(
            <ContextMenu
              menuItems={[
                {code: "EDIT", label: "Edit Costing", icon: <MonetizationOn/>, action: ()=>{editCosting(rowData)}},
                {code: "VIEW", label: "View Costing", icon: <Preview/>, action: ()=>{viewCosting(rowData)}},
              ]}
            >
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  viewCosting(rowData);
                }}
              >
                {rowData.uniqueCostingNo}
              </Link>
            </ContextMenu>
          ) : '';
        }
      },
    ];
  };

  const showExpired = () => {
    setState({...state, showExpired: !state.showExpired});
  }

  const {
    toggleCancelled,
    AddScheduledTripDialog, openAddSchedule,
    // EditScheduledTripDialog, openEditSchedule,
  } = dialogs(SystemData, apiSitePrefix, year, month, extraKeys, handleAddScheduledTripTree, handleUpdateScheduledTripTree, null, formatChars, classes, defaultValues?.tripOwnerDepartment);

  const openEditSchedule = (rowData, parentRow, childRows, title) => {
    eventBus.trigger('EditScheduledTripDialog.open', {rowData, parentRow, childRows, title});
  }

  // const openAddSchedule = (rowData, parentRow, childRows, title) => {
  //   eventBus.trigger('AddScheduledTripDialog.open', {record: rowData});
  // }

  if (!defaultValues || __.isEmpty(defaultValues))
    getDefaultValues();

  const newTripRecordTemplate = {
    recordType: "SCHEDULE",
    frequencyType: "1",
    dow: 1,
    initialAircraft: [],
    aircraftRotation: [],
    legs:[],
    overriddenUuid: null,
    cancelled: false,
    tripNo: null
  }

  const nextSeasonsMinDate = moment().get('month') > 6 ? moment.utc().startOf('year').add(1, 'year') : moment.utc().startOf('week').add(2, 'week');
  const nextSeasonsMaxDate = moment.utc(nextSeasonsMinDate).endOf('year');

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          rowStyle: (rowData)=> (rowData.recordType==="SCHEDULE"?{opacity: rowData.cancelled?0.5:1}:{backgroundColor: "#f4f4f4",opacity: rowData.cancelled?0.5:1}),
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          // restoreOpenDetailPanel: true,
          // resetOpenDetailPanel: {reset, callBack: (state) => {
          //   setReset(false);
          // }}
        }}
        actions={[
          {
            hidden: !state.canEdit ,
            disabled: false,
            icon: ()=> ( <FlightScheduleIcon style={{width: 24, height: 24 }}/> ),
            tooltip: "Generate Seasonal Schedule (SSIM)",
            isFreeAction: true,
            onClick: ()=>{generateSsim()}
          },
          {
            hidden: !state.canEdit ,
            disabled: false,
            icon: ()=> ( <AddBox style={{width: 30, height: 30 }}/> ),
            tooltip: "Add Scheduled Trip",
            isFreeAction: true,
            onClick: ()=>{openAddSchedule({...(__.cloneDeep(newTripRecordTemplate))}, null, 'Add Scheduled Trip')}
          },
          {
            hidden: !state.canEdit ,
            disabled: false,
            icon: ()=> ( <Queue style={{width: 30, height: 30 }}/> ),
            tooltip: "Add Scheduled Season Trip",
            isFreeAction: true,
            onClick: ()=>{openAddSchedule({...(__.cloneDeep(newTripRecordTemplate)), startDate: nextSeasonsMinDate.toISOString(), endDate: nextSeasonsMaxDate.toISOString()}, null, 'Add Seasons Scheduled Trip')}
          },
          (rowData) => ({
            hidden: !state.canEdit ,
            disabled: (rowData.cancelled),
            icon: 'edit',
            tooltip: "Edit",
            isFreeAction: false,
            onClick: ()=>{
              const parentRow = trips.data.filter((rec) => (rec.scheduledTripOverrideUuid === rowData.overriddenUuid && rowData.overriddenUuid !== null));
              const childRows = trips.data.filter((rec) => rec.overriddenUuid === rowData.scheduledTripOverrideUuid);
              const title = rowData.overriddenUuid !== null?'Edit Scheduled Trip Override':'Edit Scheduled Trip';
              console.log(trips.data.length);
              openEditSchedule(rowData, parentRow, childRows, title)
            }
          }),
          (rowData) => ({
            hidden: !state.canEdit ,
            icon: 'queue',
            tooltip: "Create Override",
            isFreeAction: false,
            onClick: ()=>{openAddSchedule({...(__.cloneDeep(rowData)), depth: rowData.depth + 1, sorter: undefined, recordType: 'OVERRIDE', overriddenUuid: rowData.scheduledTripOverrideUuid, scheduledTripOverrideUuid:undefined, scheduledTripUuid:undefined}, [{...(__.cloneDeep(rowData))}], 'Add Scheduled Trip Override')}
          }),
          (rowData) => (rowData.cancelled
            ?{
              hidden: !state.canEdit ,
              disabled: false,
              icon: 'airplanemode_inactive',
              tooltip: "Restore Trip",
              isFreeAction: false,
              onClick: toggleCancelled
            }
            :{
              hidden: !state.canEdit ,
              disabled: false,
              icon: 'airplanemode_inactive',
              tooltip: "Cancel Trip",
              isFreeAction: false,
              onClick: toggleCancelled
            }
          ),
        ]}
        components={{
          ...defaultComponents,
          Toolbar: (toolbarProps) => {
            return (
              <div style={{ position: "relative" }}>
                <MTableToolbar {...toolbarProps} />
                <div style={{position: "absolute", left: 350, top: 16}}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>Show Expired</Typography>
                    <Switch checked={state.showExpired} onChange={showExpired}/>
                  </Stack>
                </div>
              </div>
            );
          },
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[15, 30, 45]}
            />
          ),
        }}
        title="Scheduled Trips"
        columns={getTripColumns({canEdit: state.canEdit})}
        detailPanel={
          [
            {
              tooltip: "Show Trip Sectors",
              icon: 'flight',
              render: (rowData) => {
                return (
                  <div >
                    <div style={{ backgroundColor: "#f2f2f2" }}>
                      <ScheduledTripLegList
                        tripRecord={rowData}
                        handleUpdate={handleUpdateScheduledTripTree}
                        extraKeys={extraKeys}
                        canEdit={state.canEdit}
                        classes={classes}
                      />
                    </div>
                  </div>
                );
              },
            },
            {
              tooltip: "Show Trip Cargo Details",
              icon: ()=>(<AirCargoIcon/>),
              openIcon: ()=>(<AirCargoInverseIcon/>),
              render: (rowData) => {
                const childRows = trips.data.filter((rec) => rec.overriddenUuid === rowData.scheduledTripOverrideUuid);
                return (
                  <div>
                    <div style={{ backgroundColor: "#f2f2f2" }}>
                      <ScheduledTripClients
                        canEdit={state.canEdit}
                        scheduledTripUuid={rowData.scheduledTripOverrideUuid}
                        legs={rowData.legs}
                        hasChildren={childRows && childRows.length > 0}
                      />
                    </div>
                  </div>
                );
              },
            },
          ]
        }
        data={state.showExpired?trips.data:__.filter(trips.data, (rowData) => {
          return moment.utc(rowData.endDate).isSameOrAfter(moment.utc().startOf('day'));
        })}
        editable={{
          isDeletable: (rowData) => {
            const childRows = trips.data.filter((rec) => rec.overriddenUuid === rowData.scheduledTripOverrideUuid);
            return state.canEdit && (!childRows || childRows.length === 0);
          },
          onRowDelete: (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteScheduledTripTree.mutate({oldRecord: oldData, resolve, reject, extraKeys});
              }),
        }}

      />

      <AddScheduledTripDialog
        classes={classes}
      />
      <EditScheduledTripDialog
        systemData={SystemData}
        apiSitePrefix={apiSitePrefix}
        extraKeys={extraKeys}
        handleUpdate={handleUpdateScheduledTripTree}
        formatChars={formatChars}
        classes={classes}
        canEdit={state.canEdit}
        ownerDepartment={defaultValues?.tripOwnerDepartment || "COMMERCIAL"}
      />

      <ViewCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={costingDefaultValues}
        classes={costingClasses}
      />

      <EditCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={costingDefaultValues}
        classes={costingClasses}
        handleUpdate={handleUpdateCosting}
      />

    </div>
  );

}

export default ScheduledTripList;
